import type { PatientDetail, PreConsultSummary } from "@care-by-ella/contracts";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import { SummarySections } from "./SummarySections";

// TES-521 — what the patient actually answered, openable where the doctor is.
//
// From inside a consult: "the doctor needs to be able to at
// least open the form responses." The prep pane carried a summary (empty for
// some patients) and a date; the answers themselves were only on the patient
// page. Every submission renders here as one collapsible block: what it is,
// when, its flags, the labelled summary where one exists, the raw answers
// underneath. Nothing is open by default — the doctor opens what she needs.

type Submission = PatientDetail["formSubmissions"][number];

const TEMPLATE_LABEL: Record<string, string> = {
  intake: "Hälsodeklaration (F-02)",
  prescription_transfer: "Underlag för receptövertag (F-07)",
  weekly_checkin: "Veckans check-in",
  milestone: "Månadsavstämning",
  f01: "Första formuläret (F-01)",
};

function label(key: string): string {
  return TEMPLATE_LABEL[key] ?? key;
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function FormResponses({
  submissions,
  intakeSummary,
}: {
  submissions: Submission[];
  /** The labelled pre-consult summary, shown inside the intake block. */
  intakeSummary?: PreConsultSummary | null;
}) {
  if (submissions.length === 0) {
    return <p className="text-sm text-textSecondary">Inga formulärsvar än.</p>;
  }
  // Newest first; the intake is what the consult is about, so it leads.
  const ordered = [...submissions].sort((a, b) => {
    if (a.templateKey === "intake" && b.templateKey !== "intake") return -1;
    if (b.templateKey === "intake" && a.templateKey !== "intake") return 1;
    return b.submittedAt.localeCompare(a.submittedAt);
  });
  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-sm font-display text-lg">Formulärsvar</h2>
      <div className="flex flex-col gap-xs">
        {ordered.map((s) => {
          const nFlags = s.flags.length + s.hardStops.length;
          const summary =
            s.templateKey === "intake" &&
            intakeSummary &&
            intakeSummary.sections.length > 0
              ? intakeSummary
              : null;
          return (
            <details
              key={s.id}
              className="rounded-sm border border-surfaceMuted p-sm"
            >
              <summary className="cursor-pointer text-sm">
                <span className="font-semibold">{label(s.templateKey)}</span>
                <span className="text-textSecondary">
                  {" "}
                  · {fmt(s.submittedAt)} · v{s.templateVersion}
                </span>
                {s.hardStops.length > 0 ? (
                  <span className="ml-sm rounded-pill border border-danger/40 bg-danger/10 px-sm py-[1px] text-xs text-danger">
                    {s.hardStops.length} kontraindikation
                    {s.hardStops.length === 1 ? "" : "er"}
                  </span>
                ) : nFlags > 0 ? (
                  <span className="ml-sm rounded-pill border border-warning/40 bg-warning/10 px-sm py-[1px] text-xs text-warning">
                    {nFlags} flagg{nFlags === 1 ? "a" : "or"}
                  </span>
                ) : null}
              </summary>
              <div className="mt-sm flex flex-col gap-sm">
                {nFlags > 0 ? (
                  <div className="flex flex-wrap gap-xs">
                    {s.hardStops.map((h) => (
                      <span
                        key={`hs-${h.rule}`}
                        className="rounded-pill border border-danger/40 bg-danger/10 px-sm py-[1px] text-xs text-danger"
                        title={h.rule}
                      >
                        {flagRuleLabel(h.rule)}
                      </span>
                    ))}
                    {s.flags.map((f) => (
                      <span
                        key={`f-${f.rule}`}
                        className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[1px] text-xs text-warning"
                        title={f.rule}
                      >
                        {flagRuleLabel(f.rule)}
                      </span>
                    ))}
                  </div>
                ) : null}
                {summary ? (
                  <SummarySections sections={summary.sections} />
                ) : null}
                <details>
                  <summary className="cursor-pointer text-xs text-textSecondary">
                    {summary ? "Alla svar (rådata)" : "Svar"}
                  </summary>
                  <pre className="mt-xs overflow-x-auto rounded-sm bg-surfaceMuted/40 p-sm text-xs">
                    {JSON.stringify(s.answers, null, 2)}
                  </pre>
                </details>
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}
